import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Flex, Form, Modal, Row, Space} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, getDeleteTargetPage} from '../../../utils/common'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {SELECT_SYSTEM, SET_SYSTEM_LIST, SystemContext} from './context/context'
import SystemTable from './SystemTable'
import SystemFormModal from './form'
import { notifyError } from '../../../utils/notifications'

const System = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(SystemContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef('/api/system/')

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const getSystemListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'system.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_SYSTEM_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'system.loadError' }))
    }
  }

  useEffect(() => {
    getSystemListData()
  }, [])

  useEffect(() => {
    const url = `/api/system/?search=${search}`
    currentUrl.current = url
    getSystemListData()
  }, [debouncedSearch])

  const onCreateSystem = () => {
    dispatch({
      type: SELECT_SYSTEM,
      data: { selected_system: {}, show_system_popup: true },
    })
  }

  const onEditSystem = (record) => {
    dispatch({
      type: SELECT_SYSTEM,
      data: { selected_system: record, show_system_popup: true },
    })
  }

  const deleteSystem = async (record) => {
    let res = await deleteRequest(`/api/system/${record.id}/`)
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'system.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getSystemListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'system.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'system.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'system.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => deleteSystem(record),
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.system_list?.length) return []
    return state.system_list.map((item, index) => ({
      ...item,
      key: `system-${index}`,
      rowKey: `system-${item.id}`,
      index,
    }))
  }, [state.system_list])

  return (
    <>
      {contextHolder}

      {state.show_system_popup && (
        <SystemFormModal getSystemListData={getSystemListData} />
      )}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width administration-form">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_system"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'system.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                className="full-width"
                wrapperClass="full-width"
                onChange={(value) => setSearch(value)}
              />
              <Space size="middle" align="center">
                <Button onClick={onCreateSystem}>
                  {intl.formatMessage({ id: 'system.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <SystemTable
              dataSource={dataSource}
              pagination={pagination}
              getSystemListData={getSystemListData}
              onEdit={onEditSystem}
              onDelete={confirmDelete}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default System
